import { select } from 'redux-saga/effects'
import qs from 'qs'
import config from 'config'

import ApiBase from 'app/services/api/entities/Base'

/**
 * Asistente del Asesor (hermes / contact-center API).
 *
 * Vive en otro host que el resto del dashboard, asi que no puede usar
 * `ApiBase.privateGet` (que apunta a `API_BASE_URL`). Se arma el request
 * completo, como ya hace `MessagingSagas` con `MESSAGING_API_BASE_URL`.
 *
 * El token viaja como `Bearer`, no como `JWT`: el `JwtDecodeMiddleware` de
 * hermes hace `authorization.replace('Bearer ', '')` antes de decodificar, asi
 * que con el prefijo `JWT` el token llega intacto al `jwt.decode` y responde
 * 401. Las metricas de citas no validan nada y el header les da igual; los
 * endpoints de encuesta (`/lead-surveys/me/*`) resuelven al asesor desde este
 * token, por eso todos usan el mismo formato.
 */
const ADVISOR_ASSISTANT_API_BASE_URL = config('ADVISOR_ASSISTANT_API_BASE_URL')

export const isAdvisorAssistantEnabled = () =>
  Boolean(ADVISOR_ASSISTANT_API_BASE_URL)

function* privateGet(url, params = {}) {
  const token = yield select(state => state.auth.token)

  return yield ApiBase.makeRequest({
    method: 'get',
    baseURL: ADVISOR_ASSISTANT_API_BASE_URL,
    url,
    params,
    paramsSerializer: value => qs.stringify(value, { arrayFormat: 'repeat' }),
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
}

/**
 * Metricas de citas del asesor, agregadas desde `agent_events`.
 *
 * Este endpoint NO tiene auth en hermes: filtra por el `advisor_user_id` que
 * reciba en el query. El filtro por asesor vive en el saga.
 *
 * @param {Object} params
 * @param {string} params.startDate - YYYY-MM-DD (inclusive)
 * @param {string} params.endDate - YYYY-MM-DD (inclusive)
 * @param {number} params.advisorUserId - siempre el asesor logueado
 */
function* metrics({ startDate, endDate, advisorUserId }) {
  return yield privateGet('/advisor-metrics', {
    start_date: startDate,
    end_date: endDate,
    advisor_user_id: advisorUserId,
  })
}

/**
 * KPIs de la encuesta de satisfaccion del lead (R26-884, Story 2).
 *
 * No lleva `advisorUserId`: hermes lo resuelve de `req.user.user_id`. Mandarlo
 * seria ruido — el endpoint lo ignora a proposito para que nadie lea las
 * calificaciones de otro asesor cambiando un numero en la URL.
 */
function* surveyMetrics({ dateFrom, dateTo }) {
  return yield privateGet('/lead-surveys/me/metrics', {
    dateFrom,
    dateTo,
  })
}

/**
 * Comentarios de los leads, anonimizados por el backend (RN-010): solo texto,
 * calificacion y mes. El front no recibe con que re-identificar al lead.
 */
function* surveyComments({ dateFrom, dateTo, page = 1, limit = 20 }) {
  return yield privateGet('/lead-surveys/me/comments', {
    dateFrom,
    dateTo,
    page,
    limit,
  })
}

export default {
  metrics,
  surveyMetrics,
  surveyComments,
}
